import {
  AdminHeader,
  AdminInput,
  AdminSpinner,
  AdminSwitch,
  AnimatedDiv,
  Brand,
  BrandImageComponent,
  BrandType,
  Button,
  CollapsibleComponent,
  Disclaimer,
  Div,
  ErrorToast,
  H3,
  H5,
  MenuNamesResponse,
  P,
  Radio,
  SubTitleWithTooltipIcon,
  SuccessToast,
  VDriveLogo,
  api,
  getBrandById,
  getBrandVDriveById,
  getLocationStoreName,
  getLocationVDriveById,
  groupFundingTipSchedule,
  lightTheme,
  sanitizeDeliveryAssignment,
  unGroupFundingTipSchedule,
  updateBrandVDrive,
  useMeasuredHeight,
  reportError,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { useVDrive } from 'components/src/store/vdrive';
import { UpdateBrandVDriveRequestData } from 'components/src/types/api/brand.types';
import {
  DeliveryAssignmentEntity,
  ScheduleEntity,
  VDriveProviderInfo,
} from 'components/src/types/location.types';
import { Provider, VDriveStatus } from 'components/src/types/shared.types';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { AddEditFundingTip } from './AddEditFundingTip';
import { AssignmentStrategyComponent } from './AssignmentStrategy/components/AssignmentStrategyComponent';
import { FundingTipAmountAndAddMultiple } from './FundingTipAmountAndAddMultiple';
import { FundingTipComponent } from './FundingTipComponents';
import { PickupInstructions } from './PickupInstructions';
import { ProvidersVDrive } from './ProvidersVDrive';
import {
  PinnedFleet,
  VDriveFleetPicker,
  pinnedFleetOf,
} from './VDriveFleetPicker';
import { areProvidersEqual } from './vDriveHelpers';

export const VDriveBrand = () => {
  const [loading, setLoading] = useState(false);
  const [merchantId, setMerchantId] = useState('');
  const [applyFromPhysicalLocation, setApplyFromPhysicalLocation] =
    useState(false);
  const [autoAssignDriver, setAutoAssignDriver] = useState(false);
  const [cwTeamId, setCwTeamId] = useState('');
  const [fleet, setFleet] = useState<PinnedFleet | null>(null);
  const [fleetPickerAvailable, setFleetPickerAvailable] = useState(true);
  // Shown, locked, while the brand copies its location's settings.
  const [locationFleet, setLocationFleet] = useState<PinnedFleet | null>(null);
  const [pickupInstructions, setPickupInstructions] = useState('');
  const navigate = useNavigate();
  const [providers, setProviders] =
    useState<VDriveProviderInfo[]>(providersInitialMock);
  const [isProvidersDisabled, setIsProvidersDisabled] = useState(false);
  const [fundedTipAddEditModal, setFundedTipAddEditModal] =
    useState<ScheduleEntity | null>(null);
  const [fundedTipAdd, setFundedTipAdd] = useState(false);

  const [fundingMultiple, setFundingMultiple] = useState<
    ScheduleEntity[] | null
  >(null);

  const [fundedTipSchedule, setFundedTipSchedule] = useState<ScheduleEntity[]>(
    []
  );
  const [fundingTipOrigin, setFundingTipOrigin] = useState(false);

  const [showMerchantId, setShowMerchantId] = useState(false);
  const [notActivatedVDrive, setNotActivatedVDrive] = useState(false);

  const [details, setDetails] = useState<Brand | null>(null);
  const { id } = useParams() as { id: string };

  const [exclusiveOfferEnabled, setExclusiveOfferEnabled] = useState(false);
  const [callDispatcherEnabled, setCallDispatcherEnabled] = useState(false);
  const [motoClickEnabled, setMotoClickEnabled] = useState(false);

  const [callDispatcherMinutes, setCallDispatcherMinutes] = useState<
    number | null
  >(null);
  const interceptor = useMeasuredHeight<HTMLDivElement>();
  const [interceptAllDeliveries, setInterceptAllDeliveries] = useState(true);
  const callDispatcherMinutesRef = useRef<HTMLInputElement>(null);

  const {
    brandVDrive,
    setBrandVDrive,
    deliveryAssignment,
    setDeliveryAssignment,
  } = useVDrive();

  async function handleUpdateVDrive() {
    if (showMerchantId && !applyFromPhysicalLocation && !merchantId) {
      ErrorToast('Please add Delivery Merchant ID');
      return;
    }

    if (
      callDispatcherEnabled &&
      !interceptAllDeliveries &&
      !callDispatcherMinutes
    ) {
      ErrorToast('Please add call dispatcher minutes');
      return;
    }

    const providersDisabled =
      areProvidersEqual(providers, brandVDrive?.providers) ||
      isProvidersDisabled
        ? null
        : providers
            .filter(({ enabled }) => enabled === false)
            .map(({ provider }) => provider);

    const sanitizedDeliveryAssignment = deliveryAssignment
      ? sanitizeDeliveryAssignment(
          deliveryAssignment as DeliveryAssignmentEntity
        )
      : null;

    const body: UpdateBrandVDriveRequestData = {
      applyFromPhysicalLocation: isVGrubsBrand
        ? false
        : applyFromPhysicalLocation,
      // Kept even under applyFromPhysicalLocation: the location's note is never
      // copied into the brand, so the brand needs its own either way.
      pickupInstructions: pickupInstructions.trim() || null,
      autoAssignDriver,
      providersDisabled,
      fundedTipSchedule: groupFundingTipSchedule(fundedTipSchedule),
      fundingTipOrigin,
      deliveryAssignment: sanitizedDeliveryAssignment,
      cwTeamId: cwTeamId.length ? cwTeamId : null,
      // Ignored by the backend under applyFromPhysicalLocation, and left out
      // when the environment has no vDrive key: omitted keeps the pin.
      ...(fleetPickerAvailable && { vdriveOrganizationId: fleet?.id ?? null }),
      exclusiveOfferEnabled,
      callDispatcherEnabled,
      callDispatcherMinutes:
        !callDispatcherEnabled || interceptAllDeliveries
          ? null
          : callDispatcherMinutes,
      motoClickEnabled,
    };

    if (showMerchantId) {
      body.merchantId = merchantId;
    }

    try {
      setLoading(true);
      const { data } = await updateBrandVDrive(id, body);
      SuccessToast(data.message);

      navigate(-1);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function getBrandDetails() {
    try {
      const { data } = await getBrandById(id);
      setDetails(data.data);
      return data.data;
    } catch (error) {
      reportError(error);
    }
  }

  const [storeName, setStoreName] = useState<MenuNamesResponse | null>(null);

  async function getStoreName() {
    try {
      const { data } = await getLocationStoreName(0, id);
      setStoreName(data.data);
    } catch (error) {
      reportError(error);
    }
  }

  async function handleGetVDrive() {
    try {
      setLoading(true);
      if (brandVDrive?.brand?.id !== Number(id)) {
        getStoreName();
        const brandDetails = await getBrandDetails();
        const { data } = await getBrandVDriveById(id);

        const isVGrubsBrand = brandDetails?.type === BrandType.vGrubs;

        if (
          !isVGrubsBrand &&
          data?.data?.locationStatus !== VDriveStatus.active
        ) {
          setIsProvidersDisabled(true);
        }

        setBrandVDrive(data?.data);
        setAutoAssignDriver(data?.data?.autoAssign || false);

        setApplyFromPhysicalLocation(
          isVGrubsBrand ? false : data?.data?.applyFromPhysicalLocation || false
        );
        setMerchantId(data?.data?.merchantId || '');
        setShowMerchantId(data?.data?.merchantId ? true : false);
        setFleet(pinnedFleetOf(data?.data));
        setPickupInstructions(data?.data?.pickupInstructions || '');
        setProviders(
          data?.data?.providers.filter(
            (provider: VDriveProviderInfo) =>
              String(provider.provider) !== 'unknown'
          ) || []
        );
        setFundedTipSchedule(
          data?.data?.fundedTipSchedule
            ? unGroupFundingTipSchedule(data?.data?.fundedTipSchedule)
            : []
        );
        setFundingTipOrigin(data?.data?.fundingTipOrigin || false);
        setExclusiveOfferEnabled(data?.data?.exclusiveOfferEnabled || false);
        setCallDispatcherEnabled(data?.data?.callDispatcherEnabled || false);
        setCallDispatcherMinutes(data?.data?.callDispatcherMinutes || null);
        setDeliveryAssignment(data?.data?.deliveryAssignment || null);
        setCwTeamId(data?.data?.cwTeamId || '');
        setMotoClickEnabled(data?.data?.motoClickEnabled || false);
        setInterceptAllDeliveries(
          data?.data?.callDispatcherMinutes ? false : true
        );
      } else {
        setAutoAssignDriver(brandVDrive?.autoAssign || false);
        setApplyFromPhysicalLocation(
          isVGrubsBrand
            ? false
            : brandVDrive?.applyFromPhysicalLocation || false
        );
        setMerchantId(brandVDrive?.merchantId || '');
        setShowMerchantId(brandVDrive?.merchantId ? true : false);
        setFleet(pinnedFleetOf(brandVDrive));
        setPickupInstructions(brandVDrive?.pickupInstructions || '');
        setProviders(
          brandVDrive?.providers.filter(
            (provider: VDriveProviderInfo) =>
              String(provider.provider) !== 'unknown'
          ) || []
        );
        setFundedTipSchedule(
          brandVDrive?.fundedTipSchedule
            ? unGroupFundingTipSchedule(brandVDrive?.fundedTipSchedule)
            : []
        );
        setFundingTipOrigin(brandVDrive?.fundingTipOrigin || false);
        setExclusiveOfferEnabled(brandVDrive?.exclusiveOfferEnabled || false);
        setCallDispatcherEnabled(brandVDrive?.callDispatcherEnabled || false);
        setMotoClickEnabled(brandVDrive?.motoClickEnabled || false);
        setCallDispatcherMinutes(brandVDrive?.callDispatcherMinutes || null);
        setInterceptAllDeliveries(
          brandVDrive?.callDispatcherMinutes ? false : true
        );
      }
    } catch (error) {
      if (isAxiosError(error)) {
        if (error?.response?.status !== 404) {
          reportError(error);
          setProviders([]);
        }
        if (error?.response?.status === 404 && isVGrubsBrand) {
          handleGetFundingOrigin();
          setNotActivatedVDrive(true);
        }
      } else {
        reportError(error);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGetFundingOrigin() {
    try {
      setLoading(true);
      const { data } = await api.get(`/v1/vdrive/brand/funding-tip-origin`);
      setFundedTipSchedule(
        data?.data ? unGroupFundingTipSchedule(data?.data) : []
      );
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      handleGetVDrive();
    }
  }, []);

  const locationId = details?.locationId;

  useEffect(() => {
    if (!applyFromPhysicalLocation || !locationId) return;
    let cancelled = false;
    getLocationVDriveById(locationId)
      .then(({ data }) => {
        if (!cancelled) setLocationFleet(pinnedFleetOf(data.data));
      })
      .catch((error) => reportError(error, { silent: true }));
    return () => {
      cancelled = true;
    };
  }, [applyFromPhysicalLocation, locationId]);

  const locationState = useLocation().state;

  const brandName =
    details?.name ?? brandVDrive?.brand?.name ?? locationState?.brandName ?? '';

  const brandImage = details?.imageUrl ?? locationState?.brandImage ?? '';

  const isVGrubsBrand =
    details?.type === BrandType.vGrubs
      ? true
      : brandVDrive?.brand?.isVGrubsBrand ?? false;

  const isPhysicalLocationBrand =
    details?.isLocation ?? brandVDrive?.brand?.isLocation ?? false;

  /**
   * What an empty box ends up sending, per the backend's fallback chain: only a
   * virtual vGrubs brand gets a generated note, and only the brand that is the
   * location itself falls back to the location's note.
   */
  const pickupInstructionsHelperText =
    isVGrubsBrand && !isPhysicalLocationBrand
      ? `Leave it empty to send a generated note: "Walk inside ${
          storeName?.locationName || 'the location'
        } and ask for ${brandName || 'this brand'} order." plus the address.`
      : isPhysicalLocationBrand
      ? "Leave it empty to fall back to the location's pickup instructions."
      : 'Leave it empty to send nothing.';

  const SaveButton = () => (
    <Button $selected onClick={handleUpdateVDrive}>
      Save
    </Button>
  );

  const handleChangeApplyFromPhysicalLocation = () => {
    setApplyFromPhysicalLocation((prev) => !prev);
    if (brandVDrive) {
      setBrandVDrive({
        ...brandVDrive,
        applyFromPhysicalLocation: !brandVDrive.applyFromPhysicalLocation,
      });
    }
  };

  return (
    <>
      <AdminHeader title={'Manage vDrive Brand'}></AdminHeader>

      {loading && <AdminSpinner />}
      <AnimatedDiv>
        <Div
          $m="40px 0"
          $mSM="20px 0"
          $flex
          $alignCenter
          $between
          $columnSM
          $gap="48px"
          $gapSM="20px"
        >
          <Div
            $flex
            $alignCenter
            $gap="30px"
            $gapSM="16px"
            $width="50%"
            $widthSM="100%"
          >
            <BrandImageComponent image={brandImage} Fallback={VDriveLogo} />
            <Div>
              <H3 $mb="0" $color={lightTheme.primary}>
                {brandName}
              </H3>
              <H5 $mb="0">{storeName?.locationName}</H5>
              {notActivatedVDrive && (
                <P $mt="4" $mb="0" $color={lightTheme.primary}>
                  vDrive is not activated. It will be activated once you save
                  the changes.
                </P>
              )}
              {/* {isVDriveInProgress && (
                <P $mt="8" $color={lightTheme.primary}>
                  vDrive verification is in progress.
                </P>
              )} */}
            </Div>
          </Div>
          <Div
            $flex
            $column
            $alignEnd
            $desktop
            $width="30%"
            $widthSM="100%"
            $gap="16px"
          >
            <SaveButton />
          </Div>
        </Div>
        <Div $flex $gap="48px" $gapSM="24px" $columnSM>
          <Div
            $flex
            $gap="16px"
            $gapSM="16px"
            $column
            $width="50%"
            $widthSM="100%"
          >
            {!isVGrubsBrand && (
              <Div
                $box
                $p=" 0 24px"
                $height="80px"
                $heightSM="80px"
                $flex
                $alignCenter
                $between
              >
                <H5 $m="0">Apply vDrive settings from physical location</H5>{' '}
                <AdminSwitch
                  checked={applyFromPhysicalLocation}
                  onChange={handleChangeApplyFromPhysicalLocation}
                />
              </Div>
            )}
            {!applyFromPhysicalLocation && (
              <Div
                $box
                $p=" 0 24px"
                $height="80px"
                $heightSM="80px"
                $flex
                $alignCenter
                $between
              >
                <Div>
                  <H5 $mb="4">Automatically Assign Driver</H5>
                  <Disclaimer $m="0" $color={lightTheme.lightGray}>
                    (Based on cheapest and fastest routing)
                  </Disclaimer>
                </Div>
                <AdminSwitch
                  checked={autoAssignDriver}
                  onChange={() => setAutoAssignDriver((prev) => !prev)}
                />
              </Div>
            )}

            {!applyFromPhysicalLocation && showMerchantId && (
              <AdminInput
                tooltip="First Delivery Merchant ID used to connect vDrive."
                label="Delivery Merchant ID"
                placeholder="Enter Here"
                $textCenter
                value={merchantId}
                onChange={(e) => setMerchantId(e.target.value)}
              />
            )}

            {!applyFromPhysicalLocation && (
              <AdminInput
                tooltip="Cartwheel Team ID used to connect vDrive."
                label="Team ID"
                placeholder="Enter Here"
                $textCenter
                value={cwTeamId}
                onChange={(e) => setCwTeamId(e.target.value)}
              />
            )}

            <VDriveFleetPicker
              value={applyFromPhysicalLocation ? locationFleet : fleet}
              onChange={setFleet}
              disabled={applyFromPhysicalLocation}
              helperText={
                applyFromPhysicalLocation
                  ? "Copied from the location's vDrive settings."
                  : "This brand uses only its own fleet, never the location's. Left empty, it gets the usual vDrive quotes even when the location is pinned."
              }
              onUnavailable={() => setFleetPickerAvailable(false)}
            />

            {!applyFromPhysicalLocation && (
              <FundingTipComponent
                setFundedTipSchedule={setFundedTipSchedule}
                fundedTipSchedule={fundedTipSchedule}
                setFundedTipAddEditModal={setFundedTipAddEditModal}
                setFundedTipAdd={setFundedTipAdd}
                setFundingMultiple={setFundingMultiple}
              />
            )}

            <PickupInstructions
              value={pickupInstructions}
              onChange={setPickupInstructions}
              helperText={pickupInstructionsHelperText}
            />
          </Div>

          <Div $width="50%" $widthSM="100%">
            {!applyFromPhysicalLocation && (
              <ProvidersVDrive
                providers={providers}
                setProviders={setProviders}
                disabled={isProvidersDisabled}
              />
            )}

            <>
              <Div $mt="16" $mtSM="8"></Div>

              <Div
                $width="100%"
                $box
                $p=" 0 24px"
                $height="80px"
                $heightSM="80px"
                $flex
                $alignCenter
                $between
              >
                <SubTitleWithTooltipIcon
                  mb="0"
                  text={'Use Direct Motoclick'}
                  tooltip={
                    'This will enable direct integration with motoclick and use their drivers instead of first delivery motoclick.'
                  }
                />
                <AdminSwitch
                  checked={motoClickEnabled}
                  onChange={() => setMotoClickEnabled((prev) => !prev)}
                />
              </Div>

              <Div $mt="16" $mtSM="8"></Div>
              <Div
                $width="100%"
                $box
                $p=" 0 24px"
                $height="80px"
                $heightSM="80px"
                $flex
                $alignCenter
                $between
              >
                <SubTitleWithTooltipIcon
                  mb="0"
                  text={'Enable Tracking Link Offers'}
                  tooltip={
                    'Activate this to show a tracking link card where users can enter their phone number and receive exclusive future offers.'
                  }
                />
                <AdminSwitch
                  checked={exclusiveOfferEnabled}
                  onChange={() => setExclusiveOfferEnabled((prev) => !prev)}
                />
              </Div>
              <Div $mt="16" $mtSM="8"></Div>

              <Div $width="100%" $box $p="0 24px" $flex $justifyCenter $column>
                <Div
                  $flex
                  $alignCenter
                  $between
                  $height="80px"
                  $heightSM="80px"
                >
                  <SubTitleWithTooltipIcon
                    mb="0"
                    text={'Enable Interceptor Call'}
                    tooltip={
                      'When enabled, an AI interceptor agent will automatically call the customer after the delivery is created.'
                    }
                  />
                  <AdminSwitch
                    checked={callDispatcherEnabled}
                    onChange={() => setCallDispatcherEnabled((prev) => !prev)}
                  />
                </Div>
                <CollapsibleComponent
                  expanded={callDispatcherEnabled}
                  elementHeight={interceptor.height}
                >
                  <Div
                    ref={interceptor.ref}
                    $p="24px 0"
                    $flex
                    $column
                    $gap="8px"
                    $borderTop={callDispatcherEnabled}
                    $borderColor={lightTheme.gray}
                  >
                    <Div
                      $flex
                      $alignCenter
                      $gap="12px"
                      $pointer
                      onClick={() => {
                        setInterceptAllDeliveries(true);
                      }}
                      $opacity={!interceptAllDeliveries ? '0.5' : '1'}
                    >
                      <Radio checked={interceptAllDeliveries} />
                      <P $m="0">Intercept all deliveries (after 3 minutes) </P>
                    </Div>
                    <Div
                      $flex
                      $alignCenter
                      $between
                      $gap="12px"
                      $opacity={interceptAllDeliveries ? '0.5' : '1'}
                    >
                      <Div
                        $flex
                        $alignCenter
                        $gap="12px"
                        $pointer
                        onClick={() => {
                          setInterceptAllDeliveries(false);
                          callDispatcherMinutesRef.current?.focus();
                        }}
                      >
                        <Radio checked={!interceptAllDeliveries} />
                        <P $m="0">
                          Intercept all deliveries with no drivers assigned
                          after:
                        </P>
                      </Div>
                      <Div $width="100px" $widthSM="100px" $flexNone>
                        <AdminInput
                          placeholder="0"
                          value={
                            interceptAllDeliveries
                              ? ''
                              : callDispatcherMinutes ?? ''
                          }
                          onChange={(e) => {
                            const inputValue = e.target.value.replace(
                              /[^0-9]/g,
                              ''
                            );
                            setCallDispatcherMinutes(Number(inputValue));
                          }}
                          ref={callDispatcherMinutesRef}
                          onFocus={() => setInterceptAllDeliveries(false)}
                          $textCenter
                          suffix="min"
                        />
                      </Div>
                    </Div>
                  </Div>
                </CollapsibleComponent>
              </Div>
            </>
            <Div $mt="16" $mtSM="8"></Div>
            {!applyFromPhysicalLocation && (
              <AssignmentStrategyComponent
                isCustomized={
                  brandVDrive?.deliveryAssignment !== null &&
                  brandVDrive?.deliveryAssignment !== undefined
                }
                id={id}
                isBrand
                brandName={brandName}
              />
            )}
          </Div>
        </Div>
        <Div $mobile $flex $justifyEnd $alignEnd $mt="40">
          <SaveButton />
        </Div>
      </AnimatedDiv>

      <AddEditFundingTip
        modalIsOpen={fundedTipAddEditModal}
        closeModal={() => setFundedTipAddEditModal(null)}
        setFundedTipSchedule={setFundedTipSchedule}
        edit
      />

      <AddEditFundingTip
        modalIsOpen={fundedTipAdd}
        closeModal={() => setFundedTipAdd(false)}
        setFundedTipSchedule={setFundedTipSchedule}
      />

      <FundingTipAmountAndAddMultiple
        modalIsOpen={fundingMultiple}
        closeModal={() => setFundingMultiple(null)}
        setFundedTipSchedule={setFundedTipSchedule}
        setFundingTipOrigin={setFundingTipOrigin}
        fundingTipOrigin={fundingTipOrigin}
        brandType={isVGrubsBrand ? BrandType.vGrubs : undefined}
      />
    </>
  );
};

const providersInitialMock = Object.values(Provider).map((p) => {
  return {
    provider: p,
    enabled: true,
  };
});
